import { useCompaniesInfinite } from "@/pages/companies/hooks";
import { useFilteredDeals } from "@/pages/deals/hooks";
import { useFilteredContracts } from "@/pages/contracts/hooks";
import { useLeadsInfinite } from "@/pages/leads/hooks";
import { usePeopleInfinite } from "@/entities/people/hooks";
import { isReservedPersonId } from "@/entities/people/types";
import { useUsersInfinite } from "@/entities/users/hooks";
import { usePartnersInfinite } from "@/pages/partners/hooks";
import { usePartnerContactsInfinite } from "@/pages/partners/contacts/hooks";
import {
  EmptyFilters,
  Filters,
  OptionalNameEntityData
} from "@/services/api/types";
import { getSingleSelectOption } from "@/services/hooks/entities/useEntitySelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useMemo } from "react";

// useFilteredContracts has a different argument order/return shape than the
// other entity-search hooks; adapt it to the shared (filters, search, …) shape.
const useContractsEntitySearch = (
  filters: Filters,
  search: { term: string } | undefined,
  _groupedBy: unknown,
  _pageSize: unknown,
  sortField: string,
  sortAsc: boolean,
  _fetchAll: boolean,
  enabled: boolean
) => {
  const result = useFilteredContracts(
    sortField,
    sortAsc,
    filters,
    search?.term ?? "",
    enabled
  );
  return {
    entities: result.contracts,
    isLoading: result.isLoading,
    isError: result.isError
  };
};

// Map of entity types and their useInfinite hook
const getEntityTypeHooks = (): Partial<Record<EntityType, Function>> => ({
  [EntityType.COMPANY]: useCompaniesInfinite,
  [EntityType.PERSON]: usePeopleInfinite,
  [EntityType.DEAL]: useFilteredDeals,
  [EntityType.LEAD]: useLeadsInfinite,
  [EntityType.USER]: useUsersInfinite,
  [EntityType.CONTRACT]: useContractsEntitySearch,
  [EntityType.PARTNER]: usePartnersInfinite,
  [EntityType.PARTNER_CONTACT]: usePartnerContactsInfinite
});

interface UseEntitySearchProps {
  entityType: EntityType;
  searchTerm?: string;
  filters?: Filters;
  alwaysEnabled?: boolean;
}

export const useEntitySearch = ({
  entityType,
  searchTerm = "",
  filters = EmptyFilters,
  alwaysEnabled = false
}: UseEntitySearchProps) => {
  const hook = getEntityTypeHooks()[entityType];
  if (!hook) {
    throw new Error(`No hook implemented for entity type: ${entityType}`);
  }

  const sortingField =
    entityType === EntityType.PERSON ||
    entityType === EntityType.PARTNER_CONTACT
      ? "firstName"
      : entityType === EntityType.USER
        ? "displayName"
        : "name";

  const sorting = [{ id: sortingField, desc: false }];

  const hasActiveFilters = !!(
    filters &&
    Object.keys(filters).some((key) => filters[key as keyof typeof filters])
  );
  const hasSearchTerm = !!searchTerm;

  const {
    entities: filteredOptions,
    isLoading,
    isError
  } = hook(
    filters,
    searchTerm ? { term: searchTerm } : undefined,
    undefined,
    undefined,
    sorting[0].id,
    !sorting[0].desc,
    false,
    alwaysEnabled || hasActiveFilters || hasSearchTerm
  );

  const options = useMemo(
    () =>
      (filteredOptions as OptionalNameEntityData[])
        .filter((entity) => {
          if (entityType === EntityType.PERSON) {
            return !isReservedPersonId(entity.id);
          }
          return true;
        })
        .map((entity) => getSingleSelectOption(entity, entityType)),
    [filteredOptions, entityType]
  );

  return { options, isLoading, isError };
};
